// UserMenu.jsx -- Shared user dropdown (extracted from Layout.jsx for DailyView)
// ASCII only. var declarations. ESM only. Inline styles.
import { useState, useEffect } from 'react';
import CHROME_ICONS from '../lib/chrome-icons.js';
import { labelFor } from '../lib/humanize.js';

var MENU_FONT = "'IBM Plex Sans', 'Inter', sans-serif";
var MENU_MONO = "'IBM Plex Mono', 'SF Mono', monospace";

function AdminChipsIcon() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>; }
function DevChevronIcon() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>; }

function MenuItem(props) {
  var theme = props.theme;
  var _hover = useState(false);
  var hover = _hover[0]; var setHover = _hover[1];
  var color = props.color || theme.text;
  var bg = hover && !props.disabled ? (theme.surfaceRaised || (theme.accent + '12')) : 'transparent';
  return (
    <div
      onClick={props.disabled ? undefined : props.onClick}
      onMouseEnter={function() { if (!props.disabled) setHover(true); }}
      onMouseLeave={function() { setHover(false); }}
      style={{
        padding: '7px 10px', fontSize: 12, cursor: props.disabled ? 'default' : 'pointer',
        borderRadius: 7, fontFamily: MENU_FONT,
        display: 'flex', alignItems: 'center', gap: 8,
        background: bg, opacity: props.disabled ? 0.4 : 1
      }}
    >
      {props.icon && (
        <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', color: props.iconColor || theme.textMuted }}>
          {props.icon}
        </span>
      )}
      <span style={{ flex: 1, color: color }}>{props.label}</span>
      {props.badge > 0 && (
        <span style={{
          background: theme.critical || '#EF4444', color: '#fff',
          fontSize: 8, fontFamily: MENU_MONO, borderRadius: 10,
          padding: '1px 5px', minWidth: 16, textAlign: 'center'
        }}>{props.badge}</span>
      )}
      {props.shortcut && (
        <span style={{ fontSize: 10, fontFamily: MENU_MONO, color: theme.textMuted }}>{props.shortcut}</span>
      )}
      {props.external && (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
          <polyline points="15,3 21,3 21,9"/>
          <line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
      )}
      {props.arrow && (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2.5" strokeLinecap="round">
          <polyline points="9,18 15,12 9,6"/>
        </svg>
      )}
    </div>
  );
}

function TenantItem(props) {
  var t = props.t;
  var theme = props.theme;
  var isActive = props.isActive;
  var _hover = useState(false);
  var hover = _hover[0]; var setHover = _hover[1];
  var initials = (t.name || t.tenant_id || '').substring(0, 2).toUpperCase();
  var bg = isActive ? (theme.accentLight || (theme.accent + '15')) : (hover ? (theme.surfaceRaised || (theme.accent + '08')) : 'transparent');
  return (
    <div
      onClick={function() { if (!isActive && props.onSwitch) props.onSwitch(t.tenant_id); }}
      onMouseEnter={function() { setHover(true); }}
      onMouseLeave={function() { setHover(false); }}
      style={{
        padding: '6px 10px', borderRadius: 7, cursor: isActive ? 'default' : 'pointer',
        background: bg, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 1
      }}
    >
      <div style={{
        width: 22, height: 22, borderRadius: 5, flexShrink: 0,
        background: theme.accent + '25', color: theme.accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, fontWeight: 700, fontFamily: MENU_MONO
      }}>{initials}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: theme.text, fontFamily: MENU_FONT, lineHeight: 1.3 }}>
          {t.name || (t.tenant_id === 'sprint_mode' ? 'Sprint Mode' : t.tenant_id.replace(/_/g, ' '))}
        </div>
        {(t.plan || t.role) && (
          <div style={{ fontSize: 10, color: theme.textMuted, fontFamily: MENU_FONT, lineHeight: 1.2 }}>
            {[t.plan, t.role].filter(Boolean).join(' \u00B7 ')}
          </div>
        )}
      </div>
      {isActive && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={theme.accent} strokeWidth="2.5" strokeLinecap="round">
          <polyline points="20,6 9,17 4,12"/>
        </svg>
      )}
    </div>
  );
}

function UserMenu(props) {
  var theme = props.theme;
  var user = props.user;
  var tenants = props.tenants || [];
  var currentTenant = props.currentTenant;
  var onSwitchTenant = props.onSwitchTenant;
  var hubList = props.hubList || [];
  var currentHub = props.currentHub || 'sprint_mode';
  var onSwitchHub = props.onSwitchHub;
  var onClose = props.onClose;
  var navigate = props.navigate;

  var alertCounts = props.alertCounts || { platform: 0, dev: 0 };

  var EI_ALLOWLIST = ['aaron@sprintmode.co', 'etomaso@sprintmode.co'];
  var userEmail = user && user.email;
  var isEI = userEmail && EI_ALLOWLIST.indexOf(userEmail) >= 0;

  var _notifCount = useState(0);
  var notifCount = _notifCount[0]; var setNotifCount = _notifCount[1];

  useEffect(function() {
    fetch('/api/notifications/count')
      .then(function(r) { return r.json(); })
      .then(function(d) { if (d.ok && d.data) setNotifCount(d.data.unread || 0); })
      .catch(function() {});
  }, []);

  function nav(path) { onClose(); navigate(path); }

  var showAll = tenants.length > 3;
  var displayTenants = showAll ? tenants.slice(0, 3) : tenants;

  var sep = <div style={{ borderTop: '1px solid ' + theme.border, margin: '4px 0' }} />;

  function menuIcon(key, color) {
    var svg = CHROME_ICONS[key] || '';
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color || 'currentColor'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: svg }} />;
  }

  return (
    <div style={{
      width: 260, background: theme.surface,
      border: '1px solid ' + theme.border,
      borderRadius: 12, boxShadow: theme.cardShadow || '0 8px 24px rgba(0,0,0,0.25)',
      padding: '6px', maxHeight: '80vh', overflowY: 'auto', boxSizing: 'border-box'
    }}>
      {/* Hub switcher -- personalized */}
      <div style={{ padding: '8px 10px 4px', borderBottom: '1px solid ' + theme.border, marginBottom: 2 }}>
        <div style={{ fontSize: 10, fontFamily: MENU_MONO, color: theme.textMuted, marginBottom: 6, letterSpacing: 0.5 }}>
          {(user && user.name) ? user.name.split(' ')[0].toUpperCase() + "'S HUBS" : 'YOUR HUBS'}
        </div>
        {hubList.filter(function(h) { return h.hub_type !== 'personal' && h.id !== 'personal' && h.id !== 'aaron_personal'; }).map(function(h) {
          var isActive = h.id === currentHub;
          var initials = (labelFor(h) || '').substring(0, 2).toUpperCase();
          return (
            <div key={h.id} onClick={function() { if (!isActive && onSwitchHub) onSwitchHub(h.id); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', borderRadius: 7, cursor: isActive ? 'default' : 'pointer', marginBottom: 2 }}>
              <div style={{ width: 24, height: 24, borderRadius: 6, background: theme.text, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ color: '#fff', fontSize: 8, fontWeight: 700, fontFamily: MENU_MONO }}>{initials}</span>
              </div>
              <span style={{ flex: 1, fontSize: 13, fontWeight: isActive ? 600 : 400, color: theme.text, fontFamily: MENU_FONT }}>{h.name}</span>
              {isActive ? (
                <span style={{ fontSize: 9, fontWeight: 600, color: theme.success || '#10B981', fontFamily: MENU_MONO }}>Active</span>
              ) : (
                <span onClick={function(e) { e.stopPropagation(); if (onSwitchHub) onSwitchHub(h.id); }} style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 5, background: theme.accent, color: '#fff', cursor: 'pointer' }}>Switch</span>
              )}
            </div>
          );
        })}

      </div>

      {/* Demo Hubs -- Aaron only */}
      {props.demoHubs && props.demoHubs.length > 0 && (
        <div style={{ padding: '4px 10px 4px', borderBottom: '1px solid ' + theme.border, marginBottom: 2 }}>
          <div style={{ fontSize: 10, fontFamily: MENU_MONO, color: theme.textMuted, marginBottom: 6, letterSpacing: 0.5 }}>DEMO HUBS</div>
          {props.demoHubs.map(function(dh) {
            var isActive = dh.tenant_id === currentHub;
            var label = (dh.name || dh.tenant_id).replace('DH', 'DH ');
            return (
              <div key={dh.tenant_id} onClick={function() { if (!isActive && onSwitchHub) onSwitchHub(dh.tenant_id); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', borderRadius: 7, cursor: isActive ? 'default' : 'pointer', marginBottom: 2 }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, background: '#F59E0B22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#F59E0B', fontSize: 8, fontWeight: 700, fontFamily: MENU_MONO }}>DH</span>
                </div>
                <span style={{ flex: 1, fontSize: 13, fontWeight: isActive ? 600 : 400, color: theme.text, fontFamily: MENU_FONT }}>{label}</span>
                {isActive ? (
                  <span style={{ fontSize: 9, fontWeight: 600, color: theme.warning, fontFamily: MENU_MONO }}>Active</span>
                ) : (
                  <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 5, background: theme.warning, color: '#fff', cursor: 'pointer' }}>Switch</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Active menu items */}
      <MenuItem theme={theme} label="Settings" shortcut={'\u21E7\u2318,'} icon={menuIcon('menu_settings')} onClick={function() { nav('/settings'); }} />
      <MenuItem theme={theme} label="Admin" icon={menuIcon('menu_organization')} badge={alertCounts.dev} onClick={function() { nav('/admin?focus=platform'); }} />
      {isEI && (
        <MenuItem theme={theme} label="Dev Admin" icon={menuIcon('menu_console')} onClick={function() { nav('/admin?focus=dev'); }} />
      )}

      {sep}

      {/* Legal -- active */}
      <a href="/legal/terms" target="_blank" rel="noopener" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 10px', borderRadius: 6,
        cursor: 'pointer', textDecoration: 'none',
        fontSize: 13, color: theme.text, fontFamily: MENU_FONT
      }}>
        <span style={{ color: theme.textMuted, display: 'flex', alignItems: 'center' }}>{menuIcon('menu_legal')}</span>
        <span style={{ flex: 1 }}>Legal</span>
      </a>

      {/* Grayed placeholders */}
      {[
        { label: 'Developers',  icon: menuIcon('menu_developers') },
        { label: 'Docs',        icon: menuIcon('menu_docs') },
        { label: 'Get Help',    icon: menuIcon('menu_help') }
      ].map(function(item) {
        return (
          <div key={item.label} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 10px', borderRadius: 6,
            opacity: 0.35, cursor: 'default', pointerEvents: 'none',
            fontSize: 13, color: theme.text, fontFamily: MENU_FONT
          }}>
            <span style={{ color: theme.textMuted, display: 'flex', alignItems: 'center' }}>{item.icon}</span>
            <span style={{ flex: 1 }}>{item.label}</span>
            <span style={{ fontSize: 9, color: theme.textMuted }}>Coming Soon</span>
          </div>
        );
      })}

      {sep}

      <div style={{ padding: '4px 10px 2px', fontSize: 10, color: theme.textMuted, fontFamily: MENU_MONO }}>
        {user && user.email}
      </div>
      <MenuItem
        theme={theme} label="Log out"
        color={theme.critical || '#EF4444'}
        iconColor={theme.critical || '#EF4444'}
        icon={menuIcon('menu_logout', theme.critical || '#EF4444')}
        onClick={function() {
          try { localStorage.clear(); } catch(e) {}
          try { sessionStorage.clear(); } catch(e) {}
          try {
            document.cookie.split(';').forEach(function(c) {
              var name = c.split('=')[0].trim();
              if (name.indexOf('CF_') >= 0 || name.indexOf('cf_') >= 0) {
                document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.sprintmode.co';
                document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
              }
            });
          } catch(e) {}
          window.location.href = 'https://nomadahq.cloudflareaccess.com/cdn-cgi/access/logout';
        }}
      />
    </div>
  );
}

// Impersonate: team roster (Aaron-only feature)
var TEAM = [
  { email: 'aaron@sprintmode.co',   name: 'Aaron',   role: 'CAIO' },
  { email: 'hugh@sprintmode.co',    name: 'Hugh',    role: 'CEO' },
  { email: 'clayton@sprintmode.co', name: 'Clayton', role: 'Capital' },
  { email: 'dani@sprintmode.co',    name: 'Dani',    role: 'Studios' },
  { email: 'etomaso@sprintmode.co', name: 'Eugenio', role: 'Engineering' },
  { email: 'nikola@sprintmode.co',  name: 'Nikola',  role: 'Platform Ops' },
  { email: 'elle@sprintmode.co',    name: 'Elle',    role: 'Finance Ops' },
];

export { MenuItem, TenantItem, UserMenu, TEAM };
export default UserMenu;
